import React, { useEffect, useState, useRef } from "react";
import { getDatabase, ref, onValue, get } from "firebase/database";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import { Eye, Download, ArrowLeft, Info, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./UserProofOfDelivery.css";

const UserProofOfDelivery = () => {
  const { userId, loading: userLoading } = useGetCurrentUserId();
  const [orderDocuments, setOrderDocuments] = useState([]);
  const [fullOrders, setFullOrders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);

  const navigate = useNavigate();
  const currentUserIdRef = useRef(userId); // To prevent stale closures

  // Reset everything when userId changes
  useEffect(() => {
    currentUserIdRef.current = userId;
    setOrderDocuments([]);
    setFullOrders({});
    setIsLoading(true);
  }, [userId]);

  useEffect(() => {
    if (userLoading || !userId) {
      setIsLoading(false);
      return;
    }

    const db = getDatabase();
    const docsRef = ref(db, "transport_documents");
    const ordersRef = ref(db, "transport_orders");
    const profilesRef = ref(db, "transporter-profile");

    let unsubDocs, unsubOrders;

    const loadData = async () => {
      setIsLoading(true);

      try {
        const profileSnap = await get(profilesRef);
        const profiles = profileSnap.val() || {};

        unsubDocs = onValue(docsRef, (snapshot) => {
          // Safety check - prevent old listener from updating state
          if (currentUserIdRef.current !== userId) return;

          const allData = snapshot.val() || {};
          const processed = [];

          Object.keys(allData).forEach((transporterId) => {
            const transporterData = allData[transporterId];
            const profile = profiles[transporterId] || {};

            Object.keys(transporterData).forEach((orderId) => {
              if (orderId === "POD" || orderId === "invoices") return;

              const orderLevel = transporterData[orderId];
              const pods = [];
              const invoices = [];

              // PODs
              if (orderLevel?.POD) {
                Object.keys(orderLevel.POD).forEach(key => {
                  const pod = orderLevel.POD[key];
                  if (pod?.clientId === userId) {
                    pods.push({ id: key, ...pod });
                  }
                });
              }

              // Invoices (Nested under orderId)
              if (orderLevel?.invoices) {
                Object.entries(orderLevel.invoices).forEach(([key, inv]) => {
                  if (inv?.clientId === userId) {
                    invoices.push({
                      id: key,
                      ...inv,
                    });
                  }
                });
              }
              if (pods.length > 0 || invoices.length > 0) {
                processed.push({
                  orderId,
                  transporterId,
                  transporterName: profile.companyName || profile.contactPerson || "Unknown",
                  transporterPhone: profile.phone || "N/A",
                  pods: pods.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0)),
                  invoices: invoices.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0)),
                });
              }
            });
          });

          setOrderDocuments(processed);
          setIsLoading(false);
        });

        unsubOrders = onValue(ordersRef, (snapshot) => {
          if (currentUserIdRef.current !== userId) return;

          const orderMap = {};
          const data = snapshot.val() || {};

          Object.values(data).forEach(transporterOrders => {
            Object.values(transporterOrders || {}).forEach(order => {
              if (order?.clientId === userId && order?.orderId) {
                orderMap[order.orderId] = order;
              }
            });
          });
          setFullOrders(orderMap);
        });

      } catch (err) {
        console.error("Error loading documents:", err);
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      if (unsubDocs) unsubDocs();
      if (unsubOrders) unsubOrders();
    };
  }, [userId, userLoading]);

  const handleDownload = (url) => window.open(url, "_blank");

  const openOrderDetail = (orderId) => {
    const fullOrder = fullOrders[orderId];
    setSelectedOrderDetail(fullOrder);
    setShowDetailPopup(true);
  };

  if (userLoading || isLoading) {
    return <div className="user-pod-loading">Loading documents...</div>;
  }

  return (
    <div className="user-pod-container">
      <div className="user-pod-header">
        <button className="user-pod-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} /> <span>Back</span>
        </button>
        <h1 className="user-pod-title">Documents</h1>
        <button onClick={() => setShowInfo(true)} className="user-pod-info-btn">
          <Info size={24} />
        </button>
      </div>

      {showInfo && (
        <div className="user-pod-overlay">
          <div className="user-pod-modal">
            <button className="user-pod-close" onClick={() => setShowInfo(false)}>
              <X size={24} />
            </button>
            <h4 className="mb-3 text-center">Proof of Delivery</h4>
            <p>All uploaded Proof of Delivery (POD) documents and related invoices are shown here.</p>
          </div>
        </div>
      )}

      {orderDocuments.length === 0 ? (
        <p className="user-pod-no-data">No Proof of Delivery uploaded yet</p>
      ) : (
        <div className="user-pod-grid">
          {orderDocuments.map((doc) => {
            const order = fullOrders[doc.orderId];

            return (
              <div key={doc.orderId} className="user-pod-card">
                <div className="user-pod-card-header">
                  <div>
                    <p className="user-pod-order-id">Order #{doc.orderId}</p>
                    <p className="user-pod-route">{order?.from} → {order?.to || "N/A"}</p>
                  </div>
                  <button onClick={() => openOrderDetail(doc.orderId)} className="user-pod-view-more">
                    View More <Eye size={18} />
                  </button>
                </div>

                <div className="user-pod-info-grid">
                  <div><span>Transporter:</span> <strong>{doc.transporterName}</strong></div>
                  <div><span>Phone:</span> <strong>{doc.transporterPhone}</strong></div>
                </div>

                <div className="user-pod-section">
                  <p className="user-pod-section-title">Proof of Delivery ({doc.pods.length})</p>
                  {doc.pods.length > 0 ? (
                    <div className="user-pod-files-scroll">
                      {doc.pods.map((pod) => (
                        <div key={pod.id} className="user-pod-file-row">
                          <div className="user-pod-file-info">
                            <p className="user-pod-filename">{pod.fileName}</p>
                            <p className="user-pod-date">
                              {new Date(pod.uploadedAt).toLocaleDateString()} • {pod.truckNumber}
                            </p>
                          </div>
                          <button onClick={() => handleDownload(pod.fileUrl)} className="user-pod-download-btn">
                            <Download size={18} /> View
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="user-pod-no-invoice">No POD uploaded yet</p>
                  )}
                </div>

                <div className="user-pod-section">
                  <p className="user-pod-section-title">Invoices ({doc.invoices.length})</p>
                  {doc.invoices.length > 0 ? (
                    <div className="user-pod-files-scroll">
                      {doc.invoices.map((inv, idx) => (
                        <div key={idx} className="user-pod-file-row">
                          <div className="user-pod-file-info">
                            <p className="user-pod-filename">{inv.fileName}</p>
                            <p className="user-pod-date">{new Date(inv.uploadedAt).toLocaleDateString()}</p>
                          </div>
                          <button onClick={() => handleDownload(inv.fileUrl)} className="user-pod-download-btn">
                            <Download size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="user-pod-no-invoice">No invoices uploaded yet</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Popup */}
      {showDetailPopup && selectedOrderDetail && (
        <div className="user-pod-detail-overlay">
          <div className="user-pod-detail-modal">
            <div className="user-pod-detail-header">
              <h3 className="user-pod-detail-title">Order Details</h3>
              <button onClick={() => setShowDetailPopup(false)} className="user-pod-detail-close">
                <X size={28} />
              </button>
            </div>

            <div className="user-pod-detail-content">
              <div className="user-pod-detail-grid">
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Order ID</span>
                  <strong>{selectedOrderDetail.orderId}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Status</span>
                  <strong>{selectedOrderDetail.status || "N/A"}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Total Quantity</span>
                  <strong>{selectedOrderDetail.quantity} {selectedOrderDetail.quantityUnit}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">Remaining</span>
                  <strong>
                    {(selectedOrderDetail.quantity || 0) - (selectedOrderDetail.dispatchSummary?.totalLoad || 0)} {selectedOrderDetail.quantityUnit}
                  </strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">From</span>
                  <strong>{selectedOrderDetail.from}</strong>
                </div>
                <div className="user-pod-detail-item">
                  <span className="user-pod-detail-label">To</span>
                  <strong>{selectedOrderDetail.to}</strong>
                </div>
              </div>
            </div>

            <button onClick={() => setShowDetailPopup(false)} className="user-pod-detail-close-btn">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProofOfDelivery;